// =============================================================================
// features/sales/campaigns/detail/components/RemoveMemberDialog.tsx —
// Remoção de um membro da carteira, com motivo obrigatório
// (`PATCH /:id/members/:memberId`, F35-S04, LGPD §8.5: motivo auditado só
// como indicador de presença). Mesmo padrão de erro-preserva-formulário de
// operate/components/ReturnMemberDialog.tsx (fora de `files_allowed` — não
// importado, só o mesmo tratamento visual/foco).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { FormTextarea } from '../../../components/FormTextarea';
import { useFocusTrap } from '../../../components/useFocusTrap';

export interface RemoveMemberDialogProps {
  /** `leadId` truncado — a carteira nunca expõe nome/telefone (LGPD §8.5). */
  leadIdLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<{ ok: true } | { ok: false; message: string }>;
}

export function RemoveMemberDialog({
  leadIdLabel,
  onClose,
  onConfirm,
}: RemoveMemberDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [reason, setReason] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [isPending, setIsPending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const validationError =
    touched && reason.trim().length === 0
      ? 'Explique por que está removendo este membro.'
      : undefined;

  const handleEscape = React.useCallback(() => {
    if (!isPending) onClose();
  }, [isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setTouched(true);
    if (reason.trim().length === 0) return;

    setIsPending(true);
    setSubmitError(null);
    const result = await onConfirm(reason.trim());
    setIsPending(false);
    if (!result.ok) {
      setSubmitError(result.message);
      return;
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-member-title"
        className="w-full max-w-md rounded-md flex flex-col overflow-hidden"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex flex-col gap-1 px-6 py-5"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2
            id="remove-member-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Remover da carteira
          </h2>
          <p className="font-mono text-xs text-ink-3">{leadIdLabel}</p>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          noValidate
          className="flex flex-col gap-4 px-6 py-5"
        >
          <p className="font-sans text-sm text-ink-2 leading-relaxed">
            O membro sai definitivamente desta campanha (não volta para a fila). O motivo fica
            registrado na auditoria, mas nunca em texto pleno.
          </p>

          <FormTextarea
            id="remove-member-reason"
            label="Motivo da remoção"
            required
            placeholder="Ex.: cliente pediu para não ser mais contatado sobre esta campanha."
            disabled={isPending}
            error={validationError}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">
                Não foi possível remover
              </p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
              <p className="font-sans text-xs text-ink-3 mt-1">
                O motivo que você escreveu continua aqui — pode tentar novamente.
              </p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="submit"
              variant="danger"
              disabled={isPending}
              className="w-full justify-center"
            >
              {isPending ? 'Removendo…' : submitError ? 'Tentar novamente' : 'Confirmar remoção'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
